import classNames from "classnames";
import React from "react";
import { Button as BootstrapButton, Spinner } from "react-bootstrap";
import type { ButtonProps as BootstrapButtonProps } from "react-bootstrap";
import { Icon } from "./fm_icon";
import type { FmIconName } from "./fm_icon";

/**
 * The Dashboard button. Call sites use the design-system names (primary /
 * secondary / tertiary / destructive-*) instead of the underlying Bootstrap
 * variant classes — the mapping lives here in ONE place, so "secondary" reads
 * as "secondary" rather than "outline-primary" at every call site, and the
 * typed union makes the un-themed footgun variants (filled `secondary`,
 * `outline-muted`, `light`) unrepresentable. Appearance still comes entirely
 * from fm.css `.btn-*`.
 *
 * Wraps react-bootstrap's <Button>, forwarding its ref and all remaining props,
 * so it is a drop-in anywhere the raw button was used (inside OverlayTrigger,
 * forms, dropdowns, etc.).
 */
export type DashboardButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "destructive-primary"
  | "destructive-secondary"
  | "destructive-tertiary";

// Design name -> themed Bootstrap variant (styled by fm.css `.btn-*`). Exported so
// IconButton themes off the same single source of truth.
export const BOOTSTRAP_VARIANT: Record<DashboardButtonVariant, string> = {
  primary: "primary",
  secondary: "outline-primary",
  tertiary: "tertiary",
  "destructive-primary": "danger",
  "destructive-secondary": "outline-danger",
  "destructive-tertiary": "danger-tertiary",
};

// Filled variants carry a white label/icon; outline & text variants inherit
// their (themed) text color, so their icon uses currentColor.
export const FILLED: ReadonlySet<DashboardButtonVariant> = new Set([
  "primary",
  "destructive-primary",
]);

// Text-only variants have no fill or border to balance the glyph, so a 16px icon
// reads oversized next to the 14px label — use the smaller 12px glyph the prior
// inline text affordance used. Filled and outlined variants have a body that
// carries the 16px icon fine.
const TEXT_ONLY: ReadonlySet<DashboardButtonVariant> = new Set([
  "tertiary",
  "destructive-tertiary",
]);

// `size` is omitted: dashboard buttons are a single 14px size (see .btn in
// fm.css), so `sm`/`lg` are intentionally unrepresentable.
interface BaseDashboardButtonProps
  extends Omit<BootstrapButtonProps, "variant" | "size"> {
  /** Optional icon rendered inside the button, colored to match the variant. */
  icon?: FmIconName;
  iconPosition?: "leading" | "trailing";
  /**
   * In-flight state: disables the button, shows a small spinner in place of
   * the icon, and swaps the label for `loadingText`. The button stays mounted
   * (no layout jump) and keeps its width pressure — never replace a button
   * with a bare <Spinner /> while its action runs.
   */
  loading?: boolean;
  /**
   * Label shown while `loading` — per the copy rule, the same verb in its
   * progressive form ("Save" → "Saving…"). Falls back to `children`.
   */
  loadingText?: React.ReactNode;
}

/**
 * `compact` drops the button's padding so a text-only (tertiary) button sits
 * inline like a link — for row-level / inline actions (an "Edit" or "Add"
 * beside a field). It is only representable WITH a text-only variant: on a
 * filled or outlined button it would strip the padding the fill needs, so the
 * union below makes that combination a type error rather than a guideline.
 */
type DashboardButtonProps = BaseDashboardButtonProps &
  (
    | { compact?: false; variant?: DashboardButtonVariant }
    | { compact: true; variant: "tertiary" | "destructive-tertiary" }
  );

export const Button = React.forwardRef<HTMLButtonElement, DashboardButtonProps>(
  function Button(
    {
      variant = "primary",
      icon,
      iconPosition = "leading",
      compact = false,
      loading = false,
      loadingText,
      disabled,
      className,
      children,
      ...rest
    },
    ref
  ) {
    const iconColor = FILLED.has(variant) ? "white" : "currentColor";
    const iconSize = TEXT_ONLY.has(variant) ? 12 : 16;
    // The spinner replaces the icon (never both) and inherits the variant's
    // text color via currentColor — white on filled, themed otherwise.
    const glyph = loading ? (
      <Spinner
        as="span"
        animation="border"
        aria-hidden
        style={{ width: iconSize, height: iconSize, borderWidth: 2 }}
      />
    ) : icon ? (
      <Icon name={icon} size={iconSize} color={iconColor} />
    ) : null;
    const label = loading ? loadingText ?? children : children;

    return (
      <BootstrapButton
        ref={ref}
        variant={BOOTSTRAP_VARIANT[variant]}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={classNames(
          // justify-content-center restores .btn's own text centering, which
          // switching to flex for the glyph row otherwise overrides — visible
          // only on full-width buttons, where content-width buttons never were.
          glyph &&
            "d-inline-flex align-items-center justify-content-center gap-2",
          compact && "p-0",
          className
        )}
        {...rest}
      >
        {glyph && iconPosition === "leading" && glyph}
        {label}
        {glyph && iconPosition === "trailing" && glyph}
      </BootstrapButton>
    );
  }
);

Button.displayName = "Button";
